import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { useContext, useState } from 'react';
import MessagesApi from '../../apis/MessagesApi';
import SnackbarContext from '../../contexts/SnackbarContext';
import useApi from '../../lib/useApi';

/**
 * Props for the ContactUsDialog component.
 */
interface ContactUsDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * The ContactUsDialog component allows users to send a contact message.
 */
const ContactUsDialog = ({ open, onClose }: ContactUsDialogProps) => {
  const [message, setMessage] = useState<string>('');
  const [isSending, setIsSending] = useState<boolean>(false);
  const messagesApi = useApi(MessagesApi);
  const snackbar = useContext(SnackbarContext);

  /**
   * Handles sending the contact message.
   */
  const handleSend = async () => {
    if (message.trim() === '') {
      snackbar.setAlert(
        <Alert severity="error" variant="filled">
          Please enter a message before sending.
        </Alert>
      );
      snackbar.triggerSnackbar();
      return;
    }

    setIsSending(true);
    try {
      await messagesApi.SendMessage({ message: message });
      snackbar.setAlert(
        <Alert severity="success" variant="filled">
          Message sent successfully.
        </Alert>
      );
      snackbar.triggerSnackbar();
      setMessage('');
      onClose();
    } catch (error: any) {
      console.error('Failed to send message:', error);
      snackbar.setAlert(
        <Alert severity="error" variant="filled">
          Failed to send message. Please try again later.
        </Alert>
      );
      snackbar.triggerSnackbar();
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Contact Us</DialogTitle>
      <DialogContent>
        <DialogContentText>
          If you have any questions or need assistance, please send us a
          message. We will reply directly to the email associated with your
          account.
        </DialogContentText>
        <TextField
          autoFocus
          margin="dense"
          label="Your Message"
          type="text"
          fullWidth
          multiline
          minRows={4}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          disabled={isSending}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={isSending}
          color="secondary"
          variant="outlined"
        >
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSend}
          color="primary"
          variant="contained"
          loading={isSending}
          loadingPosition="end"
          endIcon={<SendRoundedIcon />}
        >
          Send
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default ContactUsDialog;
